import { useState } from "react";
import { supabase } from "./supabaseClient";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function SubmissionPage() {
  const [category, setCategory] = useState("Question");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState(null); // { type: 'error' | 'success', text }
  const [submitting, setSubmitting] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      setStatus({ type: "error", text: "Please enter something." });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setStatus({ type: "error", text: "No valid session token found." });
        return;
      }

      const res = await fetch(`${backendUrl}/submit-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          TYPE: category,
          content: content,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        setStatus({ type: "error", text: `Submission failed: ${errText}` });
        return;
      }

      // Clear input fields on success
      setCategory("Question");
      setContent("");
      setStatus({ type: "success", text: "Thanks! Your message was submitted." });
    } catch (err) {
      console.error(err);
      setStatus({ type: "error", text: "An error occurred while submitting." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-canvas px-4 py-12">
      <div className="w-full max-w-2xl space-y-6">
        {/* Back button */}
        <Link to="/" className="btn-ghost inline-flex w-fit">
          <ArrowLeft className="h-4 w-4" /> Back to map
        </Link>

        {/* Submission Form */}
        <form onSubmit={handleSubmit} className="card space-y-5 p-7">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">Ask a question</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Questions, feature requests, or bugs — we read every one.
            </p>
          </div>

          {/* Category Selector */}
          <div>
            <label className="label" htmlFor="feedback-category">Category</label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input"
            >
              <option>Question</option>
              <option>Request</option>
              <option>Bug</option>
            </select>
          </div>

          {/* Content */}
          <div>
            <label className="label" htmlFor="feedback-content">Message</label>
            <textarea
              id="feedback-content"
              className="input min-h-[140px] resize-y"
              placeholder="Write your message here…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit"}
          </button>

          {/* Status message */}
          {status && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                status.type === "error"
                  ? "border-danger/30 bg-danger-soft text-danger"
                  : "border-success/30 bg-success-soft text-success"
              }`}
            >
              {status.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
